<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useMutationObserver } from '@vueuse/core'
import { vResizeObserver } from '@vueuse/components'
import { faArrowLeft, faArrowRight } from '@fortawesome/duotone-regular-svg-icons'
import { isElement } from '~/utils/dom'
import Button from '~/components/ui/Button.vue'

const { nav } = defineProps<{
  nav: 'below' | 'floating'
}>()

const { t } = useI18n()

const scroller = useTemplateRef('scroller')
const index = ref(0)

const navClasses = computed(() => ({
  'flex justify-center items-center pt-8': nav === 'below',
  'absolute bottom-1/2 translate-y-1/2 left-8 right-8 flex justify-between items-center gap-6 transition-opacity': nav === 'floating',
}))

let observer: IntersectionObserver

watch((scroller), (el) => {
  if (!el)
    return

  observer = new IntersectionObserver((entries) => {
    if (!scroller.value)
      return

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        index.value = [...el.children].indexOf(entry.target)
      }
    })
  }, {
    root: scroller.value,
    rootMargin: '-50%',
  })

  onMutation()
})

function scrollTo(n: number, { focus = false, behavior = 'smooth' }: { focus?: boolean, behavior?: 'smooth' | 'instant' } = {}) {
  const item = scroller
    .value
    ?.children
    .item(n)

  item?.scrollIntoView({
    behavior,
    inline: 'center',
    block: 'nearest',
  })

  if (focus && item instanceof HTMLElement)
    item.focus()
}

function scrollBy(n: number, opt?: { focus: boolean }) {
  scrollTo(index.value + n, opt)
}

function onMutation(mutations?: MutationRecord[]) {
  if (!scroller.value || !observer)
    return

  if (!mutations) {
    for (const node of scroller.value.childNodes) {
      if (isElement(node)) {
        observer.observe(node)
      }
    }
  } else {
    mutations.forEach(({ removedNodes, addedNodes }) => {
      for (const node of removedNodes) {
        if (isElement(node)) {
          observer.unobserve(node)
        }
      }

      for (const node of addedNodes) {
        if (isElement(node)) {
          observer.observe(node)
        }
      }
    })
  }
}

function onResizeObserver() {
  nextTick(() => {
    // This prevents unwanted page scrolling on initial load
    if (scroller.value) {
      const rect = scroller.value.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0

      if (isVisible) {
        scrollTo(index.value, { behavior: 'instant' })
      }
    }
  })
}

useMutationObserver(scroller, onMutation, { childList: true })
</script>

<template>
  <div v-resize-observer="onResizeObserver" class="relative grid grid-rows-1">
    <div
      ref="scroller"
      :style="{
        scrollbarWidth: 'none',
      }"
      class="relative grid snap-x snap-mandatory auto-cols-max grid-flow-col grid-rows-1 items-center gap-(--carousel-spacing) overflow-x-scroll overflow-y-clip scroll-smooth px-[50%] py-1 focus-visible:ring [&>*]:snap-center [&>*]:snap-always"
      tabindex="0"
      @keydown.left.prevent="scrollBy(-1)"
      @keydown.right.prevent="scrollBy(1)"
    >
      <slot
        :index
        :scroll-by
        :scroll-to
        name="pre"
      />
      <slot
        :index
        :scroll-by
        :scroll-to
      />
    </div>
    <nav
      :class="[navClasses, { 'invisible opacity-0': $slots.pre && index === 0 }]"
      class="pointer-events-none z-2 transition-[opacity,visibility]"
    >
      <Button
        :aria-label="t('previous')"
        :class="{ 'pointer-events-none invisible opacity-0': !$slots.pre && index === 0 }"
        :title="t('previous')"
        class="carousel-nav-button pointer-events-auto transition-[opacity,visibility]"
        color="white"
        data-name="carousel-nav-left"
        shape="square"
        size="lg"
        @click="scrollBy(-1)"
      >
        <font-awesome-icon :icon="faArrowLeft" />
      </Button>
      <div class="flex items-center px-4 tabular-nums" data-ignore-tts>
        <slot
          :index
          name="counter"
        />
      </div>
      <Button
        :aria-label="t('next')"
        :class="{ 'pointer-events-none invisible opacity-0': index + 1 === scroller?.childElementCount }"
        :title="t('next')"
        class="carousel-nav-button pointer-events-auto transition-[opacity,visibility]"
        color="white"
        data-name="carousel-nav-right"
        shape="square"
        size="lg"
        @click="scrollBy(1)"
      >
        <font-awesome-icon :icon="faArrowRight" />
      </Button>
    </nav>
  </div>
</template>
